import React, { useMemo } from 'react'
import { SkillTileItem } from '../components/SkillGrid'
import { ROOT_LEVEL_COLORS } from '../lib/skillmap/constants'
import { darkenColor, lightenColor } from '../lib/skillmap/colorUtils'
import { getCompetencyDisplayName } from '../lib/skillmap/formatters'
import { CompetencyStatus, SkillmapData } from '../lib/skillmap/types'

type UseSkillmapGridInput = {
  data?: SkillmapData
  filterStatus: CompetencyStatus | 'all'
  collapsedCurricula: Set<number>
}

export const useSkillmapGrid = ({ data, filterStatus, collapsedCurricula }: UseSkillmapGridInput) => {
  const hierarchyLevels = useMemo(() => {
    if (!data) return new Map<number, number>()

    const levels = new Map<number, number>()
    const competencyMap = new Map(data.competencies.map((c) => [c.id, c] as const))

    const getLevel = (competencyId: number, visited = new Set<number>()): number => {
      if (visited.has(competencyId)) return 0
      visited.add(competencyId)

      if (levels.has(competencyId)) {
        return levels.get(competencyId)!
      }

      const competency = competencyMap.get(competencyId)
      if (!competency) return 0
      if (!competency.parentId) {
        levels.set(competencyId, 0)
        return 0
      }

      const parentLevel = getLevel(competency.parentId, visited)
      const level = parentLevel + 1
      levels.set(competencyId, level)
      return level
    }

    data.competencies.forEach((competency) => {
      if (!levels.has(competency.id)) {
        getLevel(competency.id)
      }
    })

    return levels
  }, [data])

  const rootCompetencies = useMemo(() => {
    if (!data) return new Map<number, number>()

    const roots = new Map<number, number>()
    const competencyMap = new Map(data.competencies.map((c) => [c.id, c] as const))

    const findRoot = (competencyId: number, visited = new Set<number>()): number => {
      if (visited.has(competencyId)) return competencyId
      visited.add(competencyId)

      if (roots.has(competencyId)) {
        return roots.get(competencyId)!
      }

      const competency = competencyMap.get(competencyId)
      if (!competency) return competencyId
      if (!competency.parentId) {
        roots.set(competencyId, competencyId)
        return competencyId
      }

      const rootId = findRoot(competency.parentId, visited)
      roots.set(competencyId, rootId)
      return rootId
    }

    data.competencies.forEach((competency) => {
      if (!roots.has(competency.id)) {
        findRoot(competency.id)
      }
    })

    return roots
  }, [data])

  const rootColors = useMemo(() => {
    if (!data) return new Map<number, { bg: string; border: string }>()

    const colors = new Map<number, { bg: string; border: string }>()
    const rootCompetencyIds = Array.from(new Set(rootCompetencies.values())).sort((a, b) => a - b)

    rootCompetencyIds.forEach((rootId, index) => {
      const colorIndex = index % ROOT_LEVEL_COLORS.length
      colors.set(rootId, ROOT_LEVEL_COLORS[colorIndex])
    })

    return colors
  }, [data, rootCompetencies])

  const gridItems = useMemo<SkillTileItem[]>(() => {
    if (!data) return []

    const filteredCompetencies = data.competencies.filter((competency) => {
      if (filterStatus !== 'all' && competency.status !== filterStatus) return false
      if (collapsedCurricula.has(competency.curriculumId)) return false
      return true
    })

    const hierarchicalNumbers = new Map<number, string>()
    const competencyMap = new Map(data.competencies.map((c) => [c.id, c] as const))
    const rootCompetencyIds = Array.from(new Set(rootCompetencies.values()))
      .filter((rootId) => {
        const comp = competencyMap.get(rootId)
        return comp && filteredCompetencies.some((c) => c.id === rootId)
      })
      .sort((a, b) => {
        const compA = competencyMap.get(a)
        const compB = competencyMap.get(b)
        if (!compA || !compB) return 0
        const sortA = compA.sortOrder ?? compA.id
        const sortB = compB.sortOrder ?? compB.id
        return sortA - sortB
      })

    const assignNumber = (competencyId: number, parentNumber: string | null, siblingIndex: number): string => {
      const number = parentNumber === null ? String(siblingIndex + 1) : `${parentNumber}.${siblingIndex + 1}`
      hierarchicalNumbers.set(competencyId, number)

      const children = filteredCompetencies
        .filter((c) => c.parentId === competencyId)
        .sort((a, b) => (a.sortOrder ?? a.id) - (b.sortOrder ?? b.id))

      children.forEach((child, index) => {
        assignNumber(child.id, number, index)
      })

      return number
    }

    rootCompetencyIds.forEach((rootId, index) => {
      assignNumber(rootId, null, index)
    })

    const sortedCompetencies = [...filteredCompetencies].sort((a, b) => {
      const numA = hierarchicalNumbers.get(a.id) || ''
      const numB = hierarchicalNumbers.get(b.id) || ''
      const partsA = numA.split('.').map(Number)
      const partsB = numB.split('.').map(Number)

      for (let i = 0; i < Math.max(partsA.length, partsB.length); i += 1) {
        const partA = partsA[i] ?? 0
        const partB = partsB[i] ?? 0
        if (partA !== partB) {
          return partA - partB
        }
      }
      return 0
    })

    const competencyColors = new Map<number, { bg: string; border: string }>()

    sortedCompetencies.forEach((competency) => {
      const level = hierarchyLevels.get(competency.id) ?? 0
      const rootId = rootCompetencies.get(competency.id) ?? competency.id
      const rootColor = rootColors.get(rootId) || ROOT_LEVEL_COLORS[0]

      if (level === 0 || level === 1) {
        competencyColors.set(competency.id, rootColor)
        return
      }

      if (level === 2) {
        const siblingIndex = sortedCompetencies
          .filter((c) => c.parentId === competency.parentId && hierarchyLevels.get(c.id) === 2)
          .sort((a, b) => (a.sortOrder ?? a.id) - (b.sortOrder ?? b.id))
          .findIndex((c) => c.id === competency.id)

        const variation = (siblingIndex % 3) * 0.1
        const darkenFactor = 0.15 + variation
        competencyColors.set(competency.id, {
          bg: darkenColor(rootColor.bg, darkenFactor),
          border: darkenColor(rootColor.border, darkenFactor)
        })
      }
    })

    sortedCompetencies.forEach((competency) => {
      const level = hierarchyLevels.get(competency.id) ?? 0
      if (level < 3) return

      const rootId = rootCompetencies.get(competency.id) ?? competency.id
      const rootColor = rootColors.get(rootId) || ROOT_LEVEL_COLORS[0]
      let parentId = competency.parentId
      let parentColor: { bg: string; border: string } | undefined

      while (parentId && !parentColor) {
        const parentLevel = hierarchyLevels.get(parentId) ?? 0
        if (parentLevel === 2) {
          parentColor = competencyColors.get(parentId)
          break
        }
        const parent = data.competencies.find((c) => c.id === parentId)
        parentId = parent?.parentId ?? null
      }

      if (parentColor) {
        competencyColors.set(competency.id, {
          bg: lightenColor(parentColor.bg, 0.3),
          border: lightenColor(parentColor.border, 0.2)
        })
      } else {
        competencyColors.set(competency.id, {
          bg: lightenColor(rootColor.bg, 0.4),
          border: lightenColor(rootColor.border, 0.3)
        })
      }
    })

    return sortedCompetencies.map((competency) => {
      const evidenceCount = competency.evidenceCount ?? 0
      let icon: string | React.ReactNode | undefined
      if (evidenceCount > 0) {
        icon = String(evidenceCount)
      } else if (competency.status === 'proficient') {
        icon = '⭐'
      } else if (competency.status === 'competent') {
        icon = '✓'
      } else if (competency.status === 'in_progress') {
        icon = '🔄'
      } else if (competency.status === 'not_yet_competent') {
        icon = '⚠️'
      } else {
        icon = '🔒'
      }

      const colorScheme = competencyColors.get(competency.id) || {
        bg: '#ffffff',
        border: '#e5e7eb'
      }

      return {
        id: `competency-${competency.id}`,
        title: getCompetencyDisplayName(competency),
        icon,
        status: competency.status,
        color: colorScheme.bg,
        borderColor: colorScheme.border,
        evidenceCount
      }
    })
  }, [collapsedCurricula, data, filterStatus, hierarchyLevels, rootColors, rootCompetencies])

  return { gridItems }
}
